import React, { useEffect, useState } from "react";
import { SecondaryBlueButton } from "@velaro/velaro-component-lib";
import useWorkflowVersions, {
  WorkflowVersionMeta
} from "@/hooks/useWorkflowVersions";

interface Props {
  workflowId: number | string;
  onRestore: (nodesJson: string, versionLabel: string) => void;
  onClose: () => void;
}

export default function WorkflowVersionHistory({
  workflowId,
  onRestore,
  onClose
}: Props) {
  const { versions, loading, error, loadVersions, getVersion } =
    useWorkflowVersions(workflowId);
  const [restoringId, setRestoringId] = useState<number | null>(null);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  async function handleRestore(v: WorkflowVersionMeta) {
    setRestoringId(v.id);
    try {
      const detail = await getVersion(v.id);
      if (!detail?.nodesJson) {
        alert("Version data could not be loaded.");
        return;
      }
      onRestore(detail.nodesJson, v.savedAt);
    } finally {
      setRestoringId(null);
    }
  }

  return (
    <div className="absolute top-0 right-0 h-full w-80 bg-white border-l border-slate-200 shadow-xl z-30 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
        <h3 className="text-sm font-semibold text-slate-800">
          Version History
        </h3>
        <button
          aria-label="Close version history"
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600 p-1 rounded focus:outline-none focus:ring-2 focus:ring-cornflower-blue-400"
        >
          ✕
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="p-4 text-sm text-slate-500 text-center">
            Loading…
          </div>
        )}
        {!loading && error && (
          <div className="p-4 text-sm text-red-600">{error}</div>
        )}
        {!loading && !error && versions.length === 0 && (
          <div className="p-4 text-sm text-slate-500 text-center">
            No saved versions yet.
            <br />
            <span className="text-xs text-slate-400 mt-1 block">
              Versions are created each time you save or publish.
            </span>
          </div>
        )}
        {!loading &&
          versions.map((v, idx) => (
            <div
              key={v.id}
              className="px-4 py-3 border-b border-slate-100 hover:bg-slate-50"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    {idx === 0 && (
                      <span className="text-xs font-medium bg-cornflower-blue-50 text-cornflower-blue-700 px-1.5 py-0.5 rounded">
                        Latest
                      </span>
                    )}
                    {v.isAutosave ? (
                      <span className="text-xs font-medium bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                        Autosave
                      </span>
                    ) : (
                      <span
                        className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                          v.isPublished
                            ? "bg-green-50 text-green-700"
                            : "bg-amber-50 text-amber-700"
                        }`}
                      >
                        {v.isPublished ? "Published" : "Draft"}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-700 font-medium truncate">
                    {v.savedAt}
                  </p>
                  <p className="text-xs text-slate-400 truncate">
                    {v.savedBy || "Unknown user"}
                  </p>
                </div>
                <button
                  onClick={() => handleRestore(v)}
                  disabled={restoringId === v.id}
                  className="text-xs text-cornflower-blue-600 hover:text-cornflower-blue-800 font-medium whitespace-nowrap disabled:opacity-50 focus:outline-none focus:underline"
                >
                  {restoringId === v.id ? "Loading…" : "Restore"}
                </button>
              </div>
            </div>
          ))}
      </div>

      {/* Footer note */}
      <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
        <p className="text-xs text-slate-400">
          Restoring loads that version into the editor. Save or Publish to
          keep it.
        </p>
      </div>
    </div>
  );
}
